import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Calendar,
  CalendarDays,
  CheckSquare,
  StickyNote,
  Check,
  X,
  Search,
} from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';

interface EntitySearchResult {
  entity_type: string;
  entity_id: string;
  name: string;
}

interface ViewMember {
  entity_type: string;
  entity_id: string;
  name: string;
  sort_order: number;
  include_types?: string[];
}

interface EntityPickerProps {
  viewId: string;
  existingMembers: ViewMember[];
  onAdd: (member: ViewMember) => void;
  onClose: () => void;
}

const ENTITY_TYPE_ICONS: Record<string, typeof Calendar> = {
  meeting_series: Calendar,
  meeting_instance: CalendarDays,
  task: CheckSquare,
  note: StickyNote,
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  meeting_series: 'Meeting Series',
  meeting_instance: 'Meeting',
  task: 'Task',
  note: 'Note',
};

const MEMBER_ENTITY_TYPES = ['meeting_series', 'meeting_instance', 'task', 'note'];
const MEETING_MEMBER_TYPES = ['meeting_series', 'meeting_instance'];

const INCLUDE_TYPE_OPTIONS = [
  { value: 'meeting_instance', label: 'Meetings', icon: CalendarDays },
  { value: 'task', label: 'Tasks', icon: CheckSquare },
  { value: 'note', label: 'Notes', icon: StickyNote },
] as const;

export function EntityPicker({ viewId, existingMembers, onAdd, onClose }: EntityPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EntitySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [pendingEntity, setPendingEntity] = useState<EntitySearchResult | null>(null);
  const [pendingIncludeTypes, setPendingIncludeTypes] = useState<Set<string>>(new Set(['meeting_instance', 'task', 'note']));
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const existingSet = new Set(existingMembers.map(m => `${m.entity_type}:${m.entity_id}`));

  const search = useCallback(async (q: string) => {
    if (q.trim().length === 0) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const data = await apiGet<EntitySearchResult[]>(`/entity-search?q=${encodeURIComponent(q)}&limit=20`);
      // Filter to only member-eligible entity types
      setResults(data.filter(r => MEMBER_ENTITY_TYPES.includes(r.entity_type)));
    } catch (err) {
      console.error('Entity search failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  function handleResultClick(result: EntitySearchResult) {
    const key = `${result.entity_type}:${result.entity_id}`;
    if (existingSet.has(key)) return;

    // For meeting types, show include_types step
    if (MEETING_MEMBER_TYPES.includes(result.entity_type)) {
      setPendingEntity(result);
      setPendingIncludeTypes(new Set(['meeting_instance', 'task', 'note']));
    } else {
      // For task/note, add directly
      addMember(result);
    }
  }

  async function addMember(result: EntitySearchResult, includeTypes?: string[]) {
    const key = `${result.entity_type}:${result.entity_id}`;
    if (existingSet.has(key)) return;

    try {
      const body: Record<string, unknown> = {
        entity_type: result.entity_type,
        entity_id: result.entity_id,
      };
      if (includeTypes && includeTypes.length > 0) {
        body.include_types = includeTypes;
      }
      const member = await apiPost<ViewMember>(`/views/${viewId}/members`, body);
      onAdd(member);
      existingSet.add(key);
      setPendingEntity(null);
    } catch (err) {
      console.error('Failed to add member:', err);
    }
  }

  function handleConfirmPending() {
    if (!pendingEntity || pendingIncludeTypes.size === 0) return;
    addMember(pendingEntity, Array.from(pendingIncludeTypes));
  }

  function togglePendingType(type: string) {
    setPendingIncludeTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        // Don't allow unchecking the last one
        if (next.size <= 1) return prev;
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (pendingEntity) {
      if (e.key === 'Escape') {
        setPendingEntity(null);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirmPending();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleResultClick(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  // Group results by entity type
  const grouped = new Map<string, EntitySearchResult[]>();
  for (const r of results) {
    if (!grouped.has(r.entity_type)) grouped.set(r.entity_type, []);
    grouped.get(r.entity_type)!.push(r);
  }

  let flatIndex = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-bg-surface border border-border rounded-xl w-full max-w-md shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search size={16} className="text-text-muted flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search meetings, tasks, notes..."
            className="flex-1 bg-transparent text-sm text-text-primary placeholder-text-muted focus:outline-none"
          />
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X size={16} />
          </button>
        </div>

        {/* Pending entity: include_types selection step */}
        {pendingEntity && (
          <div className="px-4 py-3 border-b border-border bg-bg-card/50">
            <div className="flex items-center gap-2 mb-2">
              {(() => {
                const Icon = ENTITY_TYPE_ICONS[pendingEntity.entity_type] ?? CheckSquare;
                return <Icon size={14} className="text-accent" />;
              })()}
              <span className="text-sm text-text-primary font-medium truncate">{pendingEntity.name}</span>
            </div>
            <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1.5">Include:</div>
            <div className="flex items-center gap-1.5 mb-2">
              {INCLUDE_TYPE_OPTIONS.map(opt => {
                const active = pendingIncludeTypes.has(opt.value);
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => togglePendingType(opt.value)}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${
                      active
                        ? 'bg-accent/15 border-accent/40 text-accent'
                        : 'bg-bg-input border-border text-text-muted hover:text-text-secondary hover:border-border'
                    }`}
                  >
                    <Icon size={11} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleConfirmPending}
                disabled={pendingIncludeTypes.size === 0}
                className="text-xs px-3 py-1 rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                Add
              </button>
              <button
                onClick={() => setPendingEntity(null)}
                className="text-xs px-2 py-1 text-text-muted hover:text-text-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="max-h-72 overflow-y-auto p-2">
          {loading && query.trim() && (
            <div className="text-xs text-text-muted text-center py-4">Searching...</div>
          )}
          {!loading && query.trim() && results.length === 0 && (
            <div className="text-xs text-text-muted text-center py-4">No results found</div>
          )}
          {!query.trim() && !pendingEntity && (
            <div className="text-xs text-text-muted text-center py-4">
              Type to search for meetings, tasks, or notes to add
            </div>
          )}

          {Array.from(grouped.entries()).map(([type, items]) => {
            const Icon = ENTITY_TYPE_ICONS[type] ?? CheckSquare;
            const label = ENTITY_TYPE_LABELS[type] ?? type;
            return (
              <div key={type} className="mb-2">
                <div className="flex items-center gap-1.5 px-2 py-1">
                  <Icon size={12} className="text-text-muted" />
                  <span className="text-[10px] font-medium text-text-muted uppercase tracking-wider">{label}</span>
                </div>
                {items.map(item => {
                  const isMember = existingSet.has(`${item.entity_type}:${item.entity_id}`);
                  const isPending = pendingEntity?.entity_id === item.entity_id && pendingEntity?.entity_type === item.entity_type;
                  const currentIndex = flatIndex++;
                  const isSelected = currentIndex === selectedIndex;
                  return (
                    <button
                      key={`${item.entity_type}-${item.entity_id}`}
                      onClick={() => handleResultClick(item)}
                      disabled={isMember || isPending}
                      className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
                        isMember
                          ? 'text-text-muted cursor-default'
                          : isPending
                          ? 'bg-accent/10 text-accent'
                          : isSelected
                          ? 'bg-accent/10 text-text-primary'
                          : 'text-text-secondary hover:bg-bg-hover'
                      }`}
                    >
                      <span className="flex-1 truncate">{item.name}</span>
                      {isMember && <Check size={14} className="text-accent flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
